/**
 * Tabs — token-themed underline tab bar. Controlled: the caller owns the
 * active `value`. Scrolls horizontally rather than wrapping when items
 * overflow, with edge scroll-shadows so off-screen items stay discoverable.
 *
 * Two semantics, picked with the `nav` prop:
 *   • nav=false (default) — an APG in-page tablist (role="tablist"/"tab" +
 *     aria-selected). Use when `value`/`onChange` switch *local state* and the
 *     keys are not routes (e.g. a mode switcher inside a page).
 *   • nav=true — real site navigation: a <nav> of anchor links with
 *     aria-current="page" on the active route (NO role="tab"/tablist/
 *     aria-selected, which would be the wrong pattern for links that change the
 *     URL). Keys must be in-app paths; Ctrl/Cmd/middle-click open a new tab,
 *     a plain click is intercepted for SPA navigation via `onChange`.
 *
 * Large nav bars can pass `groups` to render a 2-level, segmented sub-nav
 * (section labels + dividers) so 15-20 destinations stay scannable. Every key
 * not covered by a group falls into a trailing bucket, so no route is hidden.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';

export interface TabItem {
  key: string;
  label: ReactNode;
}

/** A logical cluster of tabs for the 2-level grouped nav. */
export interface TabGroup {
  /** Stable id (also the React key). */
  id: string;
  /** Section label rendered before the group's items. */
  label: string;
  /** Member tab keys, in display order. */
  keys: string[];
}

export interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
  /**
   * Render as site navigation (<nav> + anchor links + aria-current) instead of
   * an APG tablist. Turn this on when the keys are router paths.
   */
  nav?: boolean;
  /**
   * Optional logical grouping (only meaningful with `nav`). Keys absent from
   * every group are appended in a trailing unlabeled bucket so each route
   * stays reachable.
   */
  groups?: TabGroup[];
  'aria-label'?: string;
}

/* ------------------------------------------------------------------------- */
/* Pure logic (exported for unit tests)                                      */
/* ------------------------------------------------------------------------- */

/**
 * Longest-prefix active-route match: returns the key that is either an exact
 * match for `pathname` or a path-segment prefix of it (so nested child routes
 * keep their parent tab highlighted). Longer keys win, so `/a/b` beats `/a`.
 * Returns undefined when nothing matches (callers fall back to the first tab).
 */
export function deriveActiveKey(pathname: string, keys: string[]): string | undefined {
  return [...keys]
    .sort((a, b) => b.length - a.length)
    .find((key) => pathname === key || pathname.startsWith(`${key}/`));
}

/** A resolved group ready to render: `label === null` is the unlabeled bucket. */
export interface ResolvedTabGroup {
  id: string;
  label: string | null;
  items: TabItem[];
}

/**
 * Partition `tabs` into ordered render groups. With no groups, returns a single
 * unlabeled group holding every tab (in original order). With groups, each
 * group keeps only the keys that exist (skipping unknown keys and empty
 * groups); any tab not claimed by a group is appended to a trailing unlabeled
 * bucket so every route remains reachable.
 */
export function groupTabs(tabs: TabItem[], groups?: TabGroup[]): ResolvedTabGroup[] {
  if (!groups || groups.length === 0) {
    return [{ id: '_all', label: null, items: tabs }];
  }
  const byKey = new Map(tabs.map((t) => [t.key, t]));
  const used = new Set<string>();
  const resolved: ResolvedTabGroup[] = [];

  for (const group of groups) {
    const items: TabItem[] = [];
    for (const key of group.keys) {
      const item = byKey.get(key);
      if (item && !used.has(key)) {
        items.push(item);
        used.add(key);
      }
    }
    if (items.length > 0) {
      resolved.push({ id: group.id, label: group.label, items });
    }
  }

  const leftovers = tabs.filter((t) => !used.has(t.key));
  if (leftovers.length > 0) {
    resolved.push({ id: '_other', label: null, items: leftovers });
  }
  return resolved;
}

export interface OverflowState {
  /** More content is scrolled off the left edge. */
  left: boolean;
  /** More content remains off the right edge. */
  right: boolean;
}

/**
 * Derive the edge scroll-shadow state from a scroll container's geometry.
 * `tolerance` absorbs sub-pixel rounding so a fully-scrolled row shows no
 * phantom shadow.
 */
export function computeOverflow(
  metrics: { scrollLeft: number; scrollWidth: number; clientWidth: number },
  tolerance = 1,
): OverflowState {
  const maxScroll = metrics.scrollWidth - metrics.clientWidth;
  if (maxScroll <= tolerance) {
    return { left: false, right: false };
  }
  return {
    left: metrics.scrollLeft > tolerance,
    right: metrics.scrollLeft < maxScroll - tolerance,
  };
}

/** True for a plain primary click with no modifiers (so links can still open
 *  in a new tab via Ctrl/Cmd/Shift/middle-click). */
function isPlainClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/* ------------------------------------------------------------------------- */
/* Component                                                                 */
/* ------------------------------------------------------------------------- */

function TabInner({ label, active }: { label: ReactNode; active: boolean }) {
  return (
    <>
      {label}
      {/* Accent underline — the non-color active cue. Grows in from the centre
          when the item becomes active (gradient + soft glow, reduced-motion
          safe). */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-2 bottom-0 h-[3px] rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-200 ease-out motion-reduce:transition-none ${
          active
            ? 'scale-x-100 opacity-100 shadow-[0_0_8px_rgb(var(--color-primary)/0.55)]'
            : 'scale-x-0 opacity-0'
        }`}
      />
    </>
  );
}

// Active label uses full-contrast `text-foreground` (NOT raw `text-primary`,
// which fails WCAG AA contrast in several themes); the accent underline above
// is the non-color active signal.
const ITEM_BASE =
  'group relative -mb-px whitespace-nowrap rounded-t-token px-3.5 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const ITEM_ACTIVE = 'text-foreground';
const ITEM_INACTIVE = 'text-muted hover:bg-surface-2/60 hover:text-foreground';

export function Tabs({
  tabs,
  value,
  onChange,
  className = '',
  nav = false,
  groups,
  ...rest
}: TabsProps) {
  const ariaLabel = rest['aria-label'];
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<OverflowState>({ left: false, right: false });

  const recomputeOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setOverflow(
      computeOverflow({
        scrollLeft: el.scrollLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
      }),
    );
  }, []);

  useLayoutEffect(() => {
    recomputeOverflow();
  }, [recomputeOverflow, tabs, groups]);

  // Keep the ACTIVE tab visible: on mount and whenever `value` changes, scroll
  // it into the viewport horizontally. Without this, deep-linking (or navigating
  // via the palette) to a tab far down a long bar (e.g. Analytics #20 of 24)
  // leaves it scrolled off-screen with only an edge shadow as a hint.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = el.querySelector(
      nav ? '[aria-current="page"]' : '[aria-selected="true"]',
    );
    if (active && typeof (active as HTMLElement).scrollIntoView === 'function') {
      (active as HTMLElement).scrollIntoView({
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [value, nav, tabs]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', recomputeOverflow, { passive: true });
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(recomputeOverflow);
      observer.observe(el);
    }
    window.addEventListener('resize', recomputeOverflow);
    return () => {
      el.removeEventListener('scroll', recomputeOverflow);
      observer?.disconnect();
      window.removeEventListener('resize', recomputeOverflow);
    };
  }, [recomputeOverflow]);

  const renderItem = (tab: TabItem) => {
    const active = tab.key === value;
    const className = `${ITEM_BASE} ${active ? ITEM_ACTIVE : ITEM_INACTIVE}`;
    if (nav) {
      return (
        <a
          key={tab.key}
          href={tab.key}
          aria-current={active ? 'page' : undefined}
          onClick={(event) => {
            if (!isPlainClick(event)) return; // let the browser open a new tab
            event.preventDefault();
            onChange(tab.key);
          }}
          className={className}
        >
          <TabInner label={tab.label} active={active} />
        </a>
      );
    }
    return (
      <button
        key={tab.key}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => onChange(tab.key)}
        className={className}
      >
        <TabInner label={tab.label} active={active} />
      </button>
    );
  };

  const resolved = groupTabs(tabs, nav ? groups : undefined);
  const grouped = resolved.length > 1 || resolved.some((g) => g.label !== null);

  const scroller = (
    <div
      ref={scrollerRef}
      className="flex items-end gap-1 overflow-x-auto"
      {...(nav ? {} : { role: 'tablist' as const, 'aria-label': ariaLabel })}
    >
      {grouped
        ? resolved.map((group, index) => (
            <div key={group.id} className="flex flex-none items-end gap-1">
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className="mx-1 mb-2 h-4 w-px flex-none self-center bg-border"
                />
              )}
              {group.label && (
                <span className="mb-2 select-none self-center whitespace-nowrap pl-1 pr-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {group.label}
                </span>
              )}
              {group.items.map(renderItem)}
            </div>
          ))
        : resolved[0]?.items.map(renderItem)}
    </div>
  );

  const body = (
    <div className="relative">
      {scroller}
      {/* Edge scroll-shadows: a non-color affordance that off-screen items
          exist. Fade in only when there is actually more to scroll. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-bg to-transparent transition-opacity duration-150 ${
          overflow.left ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-bg to-transparent transition-opacity duration-150 ${
          overflow.right ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );

  if (nav) {
    return (
      <nav aria-label={ariaLabel} className={`border-b border-border ${className}`.trim()}>
        {body}
      </nav>
    );
  }

  return <div className={`border-b border-border ${className}`.trim()}>{body}</div>;
}
